/* eslint-disable @typescript-eslint/naming-convention -- resource ids are URL-style slugs */
import {
  BookOpen,
  Cog,
  DollarSign,
  FileSpreadsheet,
  Gauge,
  Grid2x2,
  History,
  Inbox,
  Lightbulb,
  Map as MapIcon,
  PenTool,
  Waves,
  type LucideIcon,
} from 'lucide-react';

/**
 * The Quote Desk's resources: every repository a quote value can come from, and the review
 * and admin pages beside them. The Resources menu lists exactly these, and a value's Source
 * names exactly one of them (plus a citation inside it), so a source is always a place the
 * person can go. Opening a cited resource — or chatting with it in Otto — hangs off the id.
 */
export type QuoteResourceId =
  | 'quote-history'
  | 'engines'
  | 'coverage'
  | 'eeaa-library'
  | 'drawings'
  | 'sound-data'
  | 'pricing'
  | 'knowledge'
  | 'pipeline-report'
  | 'scoring'
  | 'import-emails'
  | 'settings'
  | 'app-map';

export type QuoteResource = {
  id: QuoteResourceId;
  title: string;
  description: string;
  icon: LucideIcon;
  /** Where it goes; absent while the page behind it is not built. */
  to?: string;
};

export const QUOTE_RESOURCES: Record<QuoteResourceId, QuoteResource> = {
  'quote-history': {
    id: 'quote-history',
    title: 'Quote history',
    description: 'Every AES quote: have we done this before?',
    icon: History,
  },
  engines: {
    id: 'engines',
    title: 'Engines',
    description: 'Engines, spellings, ratings, connections',
    icon: Gauge,
  },
  coverage: {
    id: 'coverage',
    title: 'Coverage',
    description: 'Which cells precedent can answer',
    icon: Grid2x2,
  },
  'eeaa-library': {
    id: 'eeaa-library',
    title: 'EEAA library',
    description: 'Past workbooks as documents',
    icon: BookOpen,
  },
  drawings: {
    id: 'drawings',
    title: 'Drawings',
    description: 'Outline drawings from PDM',
    icon: PenTool,
  },
  'sound-data': {
    id: 'sound-data',
    title: 'Sound data',
    description: 'Noise sheet, OEM sheets, TL',
    icon: Waves,
  },
  pricing: {
    id: 'pricing',
    title: 'Pricing',
    description: 'Part Number Database, rates, accessories',
    icon: DollarSign,
    to: '/quotes/resources/pricing',
  },
  knowledge: {
    id: 'knowledge',
    title: 'Knowledge',
    description: 'Rules, standards, open questions',
    icon: Lightbulb,
  },
  'pipeline-report': {
    id: 'pipeline-report',
    title: 'Pipeline report',
    description: 'Eng Inbox, Results, Itemized Eval',
    icon: FileSpreadsheet,
  },
  scoring: {
    id: 'scoring',
    title: 'Scoring',
    description: "L0–L6 against AES's answer",
    icon: Gauge,
  },
  'import-emails': {
    id: 'import-emails',
    title: 'Import past emails',
    description: 'Bring Inbox emails from a date range into Quotes',
    icon: Inbox,
    // QuoteManagementPage opens its import dialog on this param.
    to: '/quotes?import=emails',
  },
  settings: {
    id: 'settings',
    title: 'Settings',
    description: 'Connections, people, configuration',
    icon: Cog,
    to: '/admin/relay-config',
  },
  'app-map': {
    id: 'app-map',
    title: 'Map of the app',
    description: 'Every page and its gaps',
    icon: MapIcon,
  },
};

/** The Resources menu's layout: two columns of headed groups, by resource id. */
export const RESOURCE_MENU: Array<
  Array<{heading: string; ids: QuoteResourceId[]}>
> = [
  [
    {heading: 'Precedent', ids: ['quote-history', 'engines', 'coverage']},
    {heading: 'Admin', ids: ['import-emails', 'settings', 'app-map']},
  ],
  [
    {
      heading: 'Repositories',
      ids: ['eeaa-library', 'drawings', 'sound-data', 'pricing', 'knowledge'],
    },
    {heading: 'Review', ids: ['pipeline-report', 'scoring']},
  ],
];
